import { useQuery } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';

// ================================
// TYPES
// ================================

export type DatePreset = 'today' | 'yesterday' | 'last7days' | 'last30days' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth';

export interface DateRangeParams {
  preset?: DatePreset;
  startDate?: string;
  endDate?: string;
}

export interface DailySalesRow {
  date: string;
  revenue: number;
  orders: number;
  subtotal: number;
  discount: number;
  vat: number;
  serviceCharge: number;
  itemsSold: number;
  avgOrderValue: number;
}

export interface DailySalesReport {
  rows: DailySalesRow[];
  totals: {
    revenue: number;
    orders: number;
    subtotal: number;
    discount: number;
    vat: number;
    serviceCharge: number;
    itemsSold: number;
    avgOrderValue: number;
  };
  dateRange: {
    start: string;
    end: string;
  };
}

export interface OrderReportItem {
  id: string;
  orderNumber: number;
  receiptNumber?: string;
  branchName: string;
  branchCode: string;
  createdBy: string;
  createdAt: string;
  completedAt?: string;
  subtotal: number;
  discount: number;
  vat: number;
  serviceCharge: number;
  total: number;
  paymentMethod: string;
  status: string;
  itemCount: number;
  items?: Array<{
    name: string;
    quantity: number;
    price: number;
    total: number;
  }>;
}

export interface OrdersReport {
  orders: OrderReportItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  summary: {
    totalRevenue: number;
    totalOrders: number;
    totalDiscount: number;
    completedOrders: number;
    cancelledOrders: number;
  };
}

export interface PaymentReportRow {
  paymentMethod: string;
  revenue: number;
  orders: number;
  avgOrderValue: number;
  percentage: number;
}

export interface PaymentReport {
  rows: PaymentReportRow[];
  totals: {
    revenue: number;
    orders: number;
    avgOrderValue: number;
  };
}

export interface ProductReportRow {
  rank: number;
  id: string;
  name: string;
  category: string;
  quantity: number;
  revenue: number;
  orderCount: number;
  avgPrice: number;
  revenuePercentage: number;
}

export interface ProductReport {
  rows: ProductReportRow[];
  totals: {
    quantity: number;
    revenue: number;
  };
}

export interface CategoryReportRow {
  id: string;
  name: string;
  quantity: number;
  revenue: number;
  uniqueItems: number;
  percentage: number;
}

export interface CategoryReport {
  rows: CategoryReportRow[];
  totals: {
    quantity: number;
    revenue: number;
  };
}

export interface HourlyReportRow {
  hour: number;
  hourLabel: string;
  revenue: number;
  orders: number;
}

export interface HourlyReport {
  rows: HourlyReportRow[];
  peakHour: {
    hour: number;
    label: string;
    revenue: number;
    orders: number;
  };
  totals: {
    revenue: number;
    orders: number;
  };
}

export interface BranchReportRow {
  branchId: string;
  branchName: string;
  branchCode: string;
  revenue: number;
  orders: number;
  discount: number;
  itemsSold: number;
  avgOrderValue: number;
  revenuePercentage: number;
}

export interface BranchReport {
  rows: BranchReportRow[];
  totals: {
    revenue: number;
    orders: number;
    discount: number;
    itemsSold: number;
    avgOrderValue: number;
  };
}

// ================================
// HOOKS
// ================================

function buildQueryParams(branchId?: string, dateParams?: DateRangeParams): string {
  const params = new URLSearchParams();
  if (branchId && branchId !== 'all') params.append('branchId', branchId);
  if (dateParams?.preset) params.append('preset', dateParams.preset);
  if (dateParams?.startDate) params.append('startDate', dateParams.startDate);
  if (dateParams?.endDate) params.append('endDate', dateParams.endDate);
  return params.toString();
}

/**
 * Fetch daily sales report
 */
export function useDailySalesReport(branchId?: string, dateParams?: DateRangeParams) {
  return useQuery({
    queryKey: ['reports', 'daily-sales', branchId, dateParams],
    queryFn: async () => {
      const params = buildQueryParams(branchId, dateParams);
      const response = await apiClient.get<APIResponse<DailySalesReport>>(
        `/reports/daily-sales?${params}`
      );
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch orders report (by bill)
 */
export function useOrdersReport(
  branchId?: string,
  dateParams?: DateRangeParams,
  page = 1,
  limit = 50,
  status?: string,
  paymentMethod?: string
) {
  return useQuery({
    queryKey: ['reports', 'orders', branchId, dateParams, page, limit, status, paymentMethod],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId && branchId !== 'all') params.append('branchId', branchId);
      if (dateParams?.preset) params.append('preset', dateParams.preset);
      if (dateParams?.startDate) params.append('startDate', dateParams.startDate);
      if (dateParams?.endDate) params.append('endDate', dateParams.endDate);
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (status) params.append('status', status);
      if (paymentMethod) params.append('paymentMethod', paymentMethod);

      const response = await apiClient.get<APIResponse<OrdersReport>>(
        `/reports/orders?${params.toString()}`
      );
      return response.data.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Fetch payment method report
 */
export function usePaymentReport(branchId?: string, dateParams?: DateRangeParams) {
  return useQuery({
    queryKey: ['reports', 'by-payment', branchId, dateParams],
    queryFn: async () => {
      const params = buildQueryParams(branchId, dateParams);
      const response = await apiClient.get<APIResponse<PaymentReport>>(
        `/reports/by-payment?${params}`
      );
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch product sales report
 */
export function useProductReport(branchId?: string, dateParams?: DateRangeParams, limit = 50) {
  return useQuery({
    queryKey: ['reports', 'by-product', branchId, dateParams, limit],
    queryFn: async () => {
      const params = buildQueryParams(branchId, dateParams);
      const response = await apiClient.get<APIResponse<ProductReport>>(
        `/reports/by-product?${params}&limit=${limit}`
      );
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch category sales report
 */
export function useCategoryReport(branchId?: string, dateParams?: DateRangeParams) {
  return useQuery({
    queryKey: ['reports', 'by-category', branchId, dateParams],
    queryFn: async () => {
      const params = buildQueryParams(branchId, dateParams);
      const response = await apiClient.get<APIResponse<CategoryReport>>(
        `/reports/by-category?${params}`
      );
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch hourly sales report
 */
export function useHourlyReport(branchId?: string, dateParams?: DateRangeParams) {
  return useQuery({
    queryKey: ['reports', 'by-hour', branchId, dateParams],
    queryFn: async () => {
      const params = buildQueryParams(branchId, dateParams);
      const response = await apiClient.get<APIResponse<HourlyReport>>(
        `/reports/by-hour?${params}`
      );
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch branch comparison report (HQ only)
 */
export function useBranchReport(dateParams?: DateRangeParams) {
  return useQuery({
    queryKey: ['reports', 'by-branch', dateParams],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateParams?.preset) params.append('preset', dateParams.preset);
      if (dateParams?.startDate) params.append('startDate', dateParams.startDate);
      if (dateParams?.endDate) params.append('endDate', dateParams.endDate);

      const response = await apiClient.get<APIResponse<BranchReport>>(
        `/reports/by-branch?${params.toString()}`
      );
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}
